import React, { useState } from "react";
import { Typography, TextField, Box, Button, Stack, Paper } from "@mui/material";

const PRESETS = [
    { label: "1 fps", interval: 1 },
    { label: "5 fps", interval: 0.2 },
    { label: "10 fps", interval: 0.1 },
    { label: "30 fps", interval: 1 / 30 },
    { label: "Max (every frame)", interval: 1 / 60 },
];

function UpdateRate({
    updateInterval,
    onUpdateRate,
    startFrame,
    endFrame,
    onContinue,
    estimatedFps,
    isDetectingFps,
    detectionMethod,
    ocrEngine = 'paddle',
    onOcrEngineChange,
}) {
    const [rate, setRate] = useState(updateInterval);
    const [rateNumber, setRateNumber] = useState(updateInterval);
    const [error, setError] = useState(false);
    const [showCustom, setShowCustom] = useState(false);

    // Dynamic presets — only show "30 fps" when we couldn't detect the real framerate
    const getPresets = () => {
        const presets = [
            { label: "1 fps", interval: 1 },
            { label: "5 fps", interval: 0.2 },
            { label: "10 fps", interval: 0.1 },
        ];

        if (estimatedFps && estimatedFps > 10) {
            const roundedFps = Math.round(estimatedFps);
            const nativeInterval = 1 / estimatedFps;

            presets.push({
                label: `${roundedFps} fps`,
                interval: nativeInterval,
            });
        } else {
            presets.push({ label: "30 fps", interval: 1 / 30 });
        }

        return presets;
    };

    const numericPresets = getPresets();

    // Re-validate when estimatedFps arrives
    React.useEffect(() => {
        if (estimatedFps && rateNumber > 0) {
            const minInterval = 1 / estimatedFps;
            if (rateNumber < minInterval) {
                setError(true);
                setShowCustom(true);
            }
        }
    }, [estimatedFps, rateNumber]);

    const applyInterval = (interval) => {
        setRate(interval);
        setRateNumber(interval);
        setError(false);
        onUpdateRate(interval);
        setShowCustom(false);
    };

    const handleChange = (event) => {
        const newNumber = parseFloat(event.target.value);
        setRate(event.target.value);

        if (isNaN(newNumber) || newNumber <= 0) {
            setError(true);
            return;
        }

        const minInterval = estimatedFps ? 1 / estimatedFps : 0;

        if (minInterval > 0 && newNumber < minInterval) {
            setError(true);
            return;
        }

        setError(false);
        setRateNumber(newNumber);
        onUpdateRate(newNumber);
    };

    // Note: FPS detection is now handled in App.js via useVideoFramerate hook.
    // We receive estimatedFps, isDetectingFps, and detectionMethod as props.

    // Rough estimate of frames (assumes ~constant rate)
    const estimatedFrames = startFrame != null && endFrame != null
        ? Math.max(1, Math.round((endFrame - startFrame) / rateNumber))
        : null;

    return (
        <Box className={"center-box"}>
            <Typography variant="h6" gutterBottom>
                Processing Rate
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How often should we sample frames? Higher rates give smoother data but take longer to process.
            </Typography>

            {/* OCR Engine / Model Selection */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" gutterBottom>Model</Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant={ocrEngine === 'paddle' ? 'contained' : 'outlined'}
                        onClick={() => onOcrEngineChange?.('paddle')}
                    >
                        PaddleOCR (Quality)
                    </Button>
                    <Button
                        size="small"
                        variant={ocrEngine === 'tesseract' ? 'contained' : 'outlined'}
                        onClick={() => onOcrEngineChange?.('tesseract')}
                    >
                        Tesseract (Speed)
                    </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    PaddleOCR is slower but significantly more accurate on most footage.
                </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" gutterBottom>Presets</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {numericPresets.map((p, idx) => (
                        <Button
                            key={idx}
                            size="small"
                            variant={Math.abs(rateNumber - p.interval) < 0.0001 ? "contained" : "outlined"}
                            onClick={() => applyInterval(p.interval)}
                        >
                            {p.label}
                        </Button>
                    ))}

                    {/* "Other" button reveals the custom interval field */}
                    <Button
                        size="small"
                        variant={showCustom ? "contained" : "outlined"}
                        onClick={() => setShowCustom(true)}
                    >
                        Other
                    </Button>
                </Stack>
                {isDetectingFps && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Detecting video framerate...
                    </Typography>
                )}
                {estimatedFps && !isDetectingFps && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Detected ≈ {estimatedFps} fps from your video
                    </Typography>
                )}
            </Paper>

            {/* Custom interval input is only shown when "Other" is selected */}
            {showCustom && (
                <>
                    <Typography variant="body2" gutterBottom>Custom interval (seconds)</Typography>
                    <TextField
                        error={error}
                        helperText={
                            error
                                ? (() => {
                                      const current = parseFloat(rate);
                                      if (!isNaN(current) && current > 0) {
                                          const minInterval = estimatedFps ? 1 / estimatedFps : 0;
                                          if (minInterval > 0 && current < minInterval) {
                                              return `Min ${(minInterval).toFixed(3)}s (~${Math.round(estimatedFps)} fps)`;
                                          }
                                      }
                                      return "Must be a positive number";
                                  })()
                                : " "
                        }
                        FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                        variant="outlined"
                        value={rate}
                        onChange={handleChange}
                        sx={{ width: 160 }}
                        autoFocus
                    />
                </>
            )}

            <Box sx={{ mt: 2 }}>
                <Typography>
                    <strong>{(1 / rateNumber).toFixed(2)}</strong> updates per second
                    {" "}({rateNumber.toFixed(3)}s interval)
                </Typography>
                {estimatedFrames != null && (
                    <Typography variant="body2" color="text.secondary">
                        Estimated frames to process: <strong>{estimatedFrames}</strong>
                    </Typography>
                )}
            </Box>

            <Button
                variant="contained"
                size="large"
                onClick={onContinue}
                disabled={error && showCustom}
                sx={{ mt: 3 }}
            >
                Continue to Review &amp; Extract
            </Button>
        </Box>
    );
}

// --- Fallback Sampling Method (used when MP4Box fails) ---

async function estimateVideoFramerateFromSampling(file) {
    return new Promise((resolve) => {
        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;

        const url = URL.createObjectURL(file);
        video.src = url;

        const timestamps = [];
        let handle = null;

        const timeout = setTimeout(() => {
            cleanup();
            resolve(null);
        }, 4000);

        const cleanup = () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
            if (handle && video.cancelVideoFrameCallback) {
                video.cancelVideoFrameCallback(handle);
            }
        };

        const callback = (now, metadata) => {
            if (metadata && typeof metadata.mediaTime === "number") {
                timestamps.push(metadata.mediaTime);
            }
            if (timestamps.length >= 30) {
                cleanup();
                resolve(calculateAverageFps(timestamps));
                return;
            }
            handle = video.requestVideoFrameCallback(callback);
        };

        video.onloadedmetadata = () => {
            if (video.requestVideoFrameCallback) {
                handle = video.requestVideoFrameCallback(callback);
            } else {
                cleanup();
                resolve(null);
            }
        };
    });
}

function calculateAverageFps(timestamps) {
    if (timestamps.length < 5) return null;

    const intervals = [];
    for (let i = 1; i < timestamps.length; i++) {
        const diff = timestamps[i] - timestamps[i - 1];
        if (diff > 0.001) intervals.push(diff);
    }

    if (intervals.length === 0) return null;

    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const fps = 1 / avgInterval;

    // Round to nearest reasonable value
    if (fps > 20 && fps < 26) return 24;
    if (fps > 24 && fps < 27) return 25;
    if (fps > 28 && fps < 32) return 30;
    if (fps > 58 && fps < 62) return 60;

    return Math.round(fps * 10) / 10;
}

export default UpdateRate;
